"use client";

import { Suspense, useEffect } from "react";
import Script from "next/script";
import { usePathname, useSearchParams } from "next/navigation";
import { capture, register } from "@/lib/analytics-client";
import { getA2gPlanAmountDollars } from "@/lib/billing-config";
import { CREDIT_PACK_CREDITS, CREDIT_PACK_PRICE_DOLLARS } from "@/lib/credit-pack";

const GA_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

type GtagFn = (...args: unknown[]) => void;

function gtag(...args: unknown[]) {
  if (typeof window === "undefined") return;
  const w = window as unknown as { gtag?: GtagFn };
  if (typeof w.gtag === "function") w.gtag(...args);
}

// Known AI-assistant referrer hosts. GA4's default "AI Assistant" channel
// group misses most of these — it buckets chatgpt.com (our largest AI source,
// ~374 sessions/mo as of 2026-06) under Referral/Direct, undercounting real AI
// traffic ~7x (62 vs ~426 sessions/mo). We classify document.referrer
// ourselves so AI-sourced traffic is measurable + attributable in GA4 + PostHog.
const AI_REFERRER_HOSTS: { test: RegExp; source: string }[] = [
  { test: /(^|\.)chatgpt\.com$/, source: "chatgpt" },
  { test: /(^|\.)chat\.openai\.com$/, source: "chatgpt" },
  { test: /(^|\.)openai\.com$/, source: "openai" },
  { test: /(^|\.)perplexity\.ai$/, source: "perplexity" },
  { test: /(^|\.)gemini\.google\.com$/, source: "gemini" },
  { test: /(^|\.)bard\.google\.com$/, source: "gemini" },
  { test: /(^|\.)copilot\.microsoft\.com$/, source: "copilot" },
  { test: /(^|\.)copilot\.com$/, source: "copilot" },
  { test: /(^|\.)claude\.ai$/, source: "claude" },
  { test: /(^|\.)doubao\.com$/, source: "doubao" },
  { test: /(^|\.)you\.com$/, source: "you" },
  { test: /(^|\.)poe\.com$/, source: "poe" },
  { test: /(^|\.)grok\.com$/, source: "grok" },
];

// Returns a normalized AI source label if the referrer is a known AI assistant,
// else null. Tolerates non-URL / empty referrers.
function classifyAiReferrer(referrerUrl: string): string | null {
  if (!referrerUrl) return null;
  try {
    const host = new URL(referrerUrl).hostname.toLowerCase();
    for (const { test, source } of AI_REFERRER_HOSTS) {
      if (test.test(host)) return source;
    }
  } catch {
    // Not a parseable URL — treat as no AI referrer.
  }
  return null;
}

// useSearchParams forces client-side rendering for the whole subtree unless it
// sits inside a Suspense boundary. Isolate it here so the rest of the layout
// stays statically renderable.
function PageViewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!GA_ID) return;
    const search = searchParams?.toString();
    const page_path = search ? `${pathname}?${search}` : pathname;
    gtag("event", "page_view", {
      page_path,
      page_location: typeof window !== "undefined" ? window.location.href : undefined,
      page_title: typeof document !== "undefined" ? document.title : undefined,
    });
  }, [pathname, searchParams]);

  // One-shot AI-referral attribution. document.referrer holds the external
  // entry referrer; classify it once per session (sessionStorage guard) so
  // internal SPA navigations don't re-fire and inflate counts.
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const KEY = "a2g_ai_referral_logged";
    try {
      if (sessionStorage.getItem(KEY)) return;
    } catch {
      // sessionStorage unavailable (privacy mode) — fall through and fire
      // once per page load rather than not at all.
    }

    const aiSource = classifyAiReferrer(document.referrer);
    try {
      sessionStorage.setItem(KEY, aiSource ?? "none");
    } catch {
      // ignore: best-effort dedupe only.
    }
    if (!aiSource) return;

    // GA4: explicit event + user property so AI traffic is reportable
    // independent of the default channel grouping (which undercounts it).
    gtag("event", "ai_referral", {
      ai_source: aiSource,
      page_location: window.location.href,
    });
    gtag("set", "user_properties", { acquisition_ai_source: aiSource });

    // PostHog: register a super property so every later event in the session
    // carries the AI attribution, plus a discrete capture for funnels.
    register({ ai_referrer: aiSource });
    capture("ai_referral", {
      ai_source: aiSource,
      referrer: document.referrer,
    });
  }, []);

  return null;
}

export function Analytics() {
  if (!GA_ID) return null;

  return (
    <>
      {/* crossOrigin is what makes a failure inside this script REPORTABLE.
          Without it the browser withholds the message, file and line from
          window.onerror and every failure arrives as a bare "Script error." —
          37 of those landed in error tracking in one week with no way to tell
          what broke. Google serves gtag.js with Access-Control-Allow-Origin: *
          (verified), so requesting CORS mode cannot stop it loading. */}
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
        strategy="afterInteractive"
        crossOrigin="anonymous"
      />
      <Script id="ga4-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', '${GA_ID}', { send_page_view: false });
        `}
      </Script>
      <Suspense fallback={null}>
        <PageViewTracker />
      </Suspense>
    </>
  );
}

// Bind the Supabase user id to GA4 so cross-device sessions stitch together
// and User-ID reports populate. Mirrors PostHogIdentify on the GA side.
export function setGAUserId(userId: string | null) {
  if (!GA_ID) return;
  gtag("config", GA_ID, { user_id: userId ?? undefined, send_page_view: false });
}

// Track custom events from anywhere in the app. Dual-fires to GA4 + PostHog.
// For GA4 ecommerce events (purchase, begin_checkout, view_item, etc.), use
// the dedicated helpers below — they enforce the required parameter shape.
export function trackEvent(
  action: string,
  category: string,
  label?: string,
  value?: number,
  props?: Record<string, unknown>
) {
  if (typeof window === "undefined") return;

  gtag("event", action, {
    event_category: category,
    event_label: label,
    value,
    ...props,
  });

  capture(action, {
    category,
    label,
    value,
    ...props,
  });
}

// GA4 ecommerce schema:
// https://developers.google.com/analytics/devguides/collection/ga4/reference/events

export type EcommerceItem = {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
};

export type CheckoutKind = "monthly" | "annual" | "credits";

const A2G_PRO_ITEM: EcommerceItem = {
  item_id: "a2g_pro",
  item_name: "audio2guitar Pro",
  item_category: "subscription",
};

const CREDIT_PACK_ITEM: EcommerceItem = {
  item_id: "a2g_payg_10",
  item_name: `audio2guitar ${CREDIT_PACK_CREDITS}-credit pack`,
  item_category: "credit_pack",
  item_variant: "payg",
  price: CREDIT_PACK_PRICE_DOLLARS,
  quantity: 1,
};

const PLAN_VARIANTS: Record<"monthly" | "annual", { variant: string; price: number }> = {
  monthly: { variant: "monthly", price: getA2gPlanAmountDollars(process.env.NEXT_PUBLIC_STRIPE_PRICE_ID) ?? 8.99 },
  annual: { variant: "annual", price: getA2gPlanAmountDollars(process.env.NEXT_PUBLIC_STRIPE_ANNUAL_PRICE_ID) ?? 59.99 },
};

function checkoutItem(kind: CheckoutKind): EcommerceItem {
  if (kind === "credits") return CREDIT_PACK_ITEM;
  return planItem(kind);
}

function planItem(plan: "monthly" | "annual"): EcommerceItem {
  const v = PLAN_VARIANTS[plan];
  return { ...A2G_PRO_ITEM, item_variant: v.variant, price: v.price, quantity: 1 };
}

export function trackViewItem(plan: "monthly" | "annual") {
  if (typeof window === "undefined") return;
  const item = planItem(plan);
  gtag("event", "view_item", {
    currency: "USD",
    value: item.price,
    items: [item],
  });
  capture("view_item", { plan, value: item.price });
}

export function trackSelectItem(plan: "monthly" | "annual") {
  if (typeof window === "undefined") return;
  const item = planItem(plan);
  gtag("event", "select_item", {
    item_list_id: "pricing_plans",
    item_list_name: "Pricing Plans",
    items: [item],
  });
  capture("select_item", { plan });
}

export function trackBeginCheckout(kind: CheckoutKind) {
  if (typeof window === "undefined") return;
  const item = checkoutItem(kind);
  gtag("event", "begin_checkout", {
    currency: "USD",
    value: item.price,
    items: [item],
  });
  capture("begin_checkout", { plan: kind, value: item.price, currency: "USD" });
}

// transaction_id MUST be the Stripe checkout session id (cs_...) so GA4
// dedupes across reloads of the success page and aligns with the server-side
// Measurement Protocol call from the Stripe webhook.
export function trackPurchase(args: {
  transactionId: string;
  plan: CheckoutKind;
  valueUsd: number;
}) {
  if (typeof window === "undefined") return;
  const baseItem = checkoutItem(args.plan);
  const item = { ...baseItem, price: args.valueUsd };
  gtag("event", "purchase", {
    transaction_id: args.transactionId,
    currency: "USD",
    value: args.valueUsd,
    items: [item],
  });
  capture("purchase", {
    transaction_id: args.transactionId,
    plan: args.plan,
    value: args.valueUsd,
    currency: "USD",
  });
}
